import { defineStore, acceptHMRUpdate } from "pinia";
import {
  JobPostingDetailsInterface,
  JobPostingInterface,
  JobPostingStatus,
} from "@/modules/job_postings/types";
import {
  apiGetJobPostingDetails,
  apiGetJobPostings,
  apiPostJobPostingInterest,
} from "@/modules/job_postings/_utils/api";
import type VueRouter from "vue-router";

interface JobPostingsPaginationInterface {
  start: number;
  next_start?: number | null;
}

export interface StateInterface {
  newJobPostings: Array<JobPostingInterface>;
  loadingNewJobPostings: boolean;
  newJobPostingsPagination: JobPostingsPaginationInterface;

  ongoingJobPostings: Array<JobPostingInterface>;
  loadingOngoingJobPostings: boolean;
  ongoingJobPostingsPagination: JobPostingsPaginationInterface;

  loadingSelectedJobPosting: boolean;
  selectedJobPosting: JobPostingInterface | undefined;
  selectedJobPostingDetails: JobPostingDetailsInterface | undefined;
}

export const useJobPostingsStore = defineStore("job_postings", {
  state: (): StateInterface => {
    return {
      newJobPostings: [],
      loadingNewJobPostings: false,
      newJobPostingsPagination: { start: 0, next_start: null },

      ongoingJobPostings: [],
      loadingOngoingJobPostings: false,
      ongoingJobPostingsPagination: { start: 0, next_start: null },

      loadingSelectedJobPosting: false,
      selectedJobPosting: undefined,
      selectedJobPostingDetails: undefined,
    };
  },
  actions: {
    getNewJobPostings(withIncrement = false) {
      if (withIncrement) {
        this.incrementNewJobPostingsPagination();
      }

      return new Promise((resolve, reject) => {
        this.setLoadingNewJobPostings(true);

        apiGetJobPostings(JobPostingStatus.NEW)
          .then((res: any) => {
            this.setNewJobPostings([...res.data.data]);
            resolve(res.data);
          })
          .catch((err: any) => {
            reject(err);
          })
          .finally(() => this.setLoadingNewJobPostings(false));
      });
    },

    getOngoingJobPostings(withIncrement = false) {
      if (withIncrement) {
        this.incrementOngoingJobPostingsPagination();
      }

      return new Promise((resolve, reject) => {
        this.setLoadingOngoingJobPostings(true);

        apiGetJobPostings(JobPostingStatus.ONGOING)
          .then((res: any) => {
            this.setOngoingJobPostings([...res.data.data]);
            resolve(res.data);
          })
          .catch((err: any) => {
            reject(err);
          })
          .finally(() => this.setLoadingOngoingJobPostings(false));
      });
    },

    showSelectedJobPostingDetails(jobPosting: JobPostingInterface, router: VueRouter): any {
      if (this.selectedJobPosting?.id === jobPosting.id) {
        return;
      }

      router.replace({ query: { id: String(jobPosting.id) } });
      this.setSelectedJobPosting(jobPosting);
      this.clearSelectedJobPostingDetails();
      this.setLoadingSelectedJobPostingDetails(true);

      return new Promise((resolve, reject) => {
        apiGetJobPostingDetails(jobPosting.id)
          .then((res: any) => {
            this.setSelectedJobPostingDetails(res.data.data);
            resolve(res.data.data);
          })
          .catch((err: any) => {
            this.clearSelectedJobPosting();
            reject(err);
          })
          .finally(() => this.setLoadingSelectedJobPostingDetails(false));
      });
    },

    hideSelectedJobPostingDetails() {
      this.clearSelectedJobPosting();
      this.clearSelectedJobPostingDetails();
    },

    sendJobPostingInterest(): any {
      if (!this.selectedJobPosting || this.selectedJobPostingDetails?.has_shown_interest) {
        return;
      }

      const selectedJobPostingId = this.selectedJobPosting.id;

      return new Promise((resolve, reject) => {
        apiPostJobPostingInterest(selectedJobPostingId)
          .then(() => {
            this.setSelectedJobPostingShowInterest();
            resolve(true);
          })
          .catch((err: any) => {
            reject(err);
          });
      });
    },

    // NEW job postings
    setNewJobPostings(newJobPostings: Array<JobPostingInterface>): void {
      this.newJobPostings = newJobPostings;
    },

    setLoadingNewJobPostings(loadingNewJobPostings: boolean): void {
      this.loadingNewJobPostings = loadingNewJobPostings;
    },

    incrementNewJobPostingsPagination(): void {
      this.newJobPostingsPagination.start = this.newJobPostingsPagination.next_start ?? 0;
    },

    // ONGOING job postings
    setOngoingJobPostings(ongoingJobPostings: Array<JobPostingInterface>): void {
      this.ongoingJobPostings = ongoingJobPostings;
    },

    setLoadingOngoingJobPostings(loadingOngoingJobPostings: boolean): void {
      this.loadingOngoingJobPostings = loadingOngoingJobPostings;
    },

    incrementOngoingJobPostingsPagination(): void {
      this.ongoingJobPostingsPagination.start = this.ongoingJobPostingsPagination.next_start ?? 0;
    },

    // Selected job posting
    setLoadingSelectedJobPostingDetails(loadingSelectedJobPosting: boolean): void {
      this.loadingSelectedJobPosting = loadingSelectedJobPosting;
    },

    setSelectedJobPosting(selectedJobPosting: JobPostingInterface): void {
      this.selectedJobPosting = selectedJobPosting;
    },

    clearSelectedJobPosting(): void {
      this.selectedJobPosting = undefined;
    },

    setSelectedJobPostingDetails(selectedJobPostingDetails: JobPostingDetailsInterface): void {
      this.selectedJobPostingDetails = selectedJobPostingDetails;
    },

    clearSelectedJobPostingDetails(): void {
      this.selectedJobPostingDetails = undefined;
    },

    setSelectedJobPostingShowInterest(): void {
      if (this.selectedJobPostingDetails) {
        this.selectedJobPostingDetails.has_shown_interest = true;
      }
    },
  },
});

// HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useJobPostingsStore, import.meta.hot));
}
